import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { listUserProjectsApi, fetchProjectByIdAPI, setCurrentProjectApi, createProjectApi, updateProjectApi, getProjectRebuildHierarchyApi } from "../../api/api";
import { Projects, CreateProjectResponse, ProjectRebuildHierarchy } from "../../api";
import type { RootState } from "../store";

interface ProjectState {
    projects: Projects[];
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    currentProject: Projects | null;
    currentProjectId: number | null;
    rebuildHierarchy: ProjectRebuildHierarchy | null;
    rebuildHierarchyStatus: "idle" | "loading" | "succeeded" | "failed";
    rebuildHierarchyError: string | null;
}

const initialState: ProjectState = {
    projects: [],
    status: "idle",
    error: null,
    currentProject: null,
    currentProjectId: null,
    rebuildHierarchy: null,
    rebuildHierarchyStatus: "idle",
    rebuildHierarchyError: null
};

export const createProject = createAsyncThunk<CreateProjectResponse, Record<string, any>, { rejectValue: string }>(
    "projects/createProject",
    async (request, { getState, rejectWithValue }) => {
        const state = getState() as RootState;
        const token = state.clientToken.token;

        if (!token) {
            return rejectWithValue("No token available");
        }

        try {
            const response = await createProjectApi(request, token);
            return response;
        } catch (error: any) {
            if (error.response && error.response.status === 409) {
                return rejectWithValue("Project with this name already exists");
            }
            return rejectWithValue(error.message || "Failed to create project");
        }
    }
);

// Async thunk to update a project
export const updateProject = createAsyncThunk<Projects, Record<string, any>, { rejectValue: string }>(
    "projects/updateProject",
    async (request, { getState, rejectWithValue }) => {
        const state = getState() as RootState;
        const token = request.idToken || state.clientToken.token;

        if (!token) {
            return rejectWithValue("No token available");
        }

        try {
            const updatedProject = await updateProjectApi(request, token);

            return updatedProject;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to update project");
        }
    }
);

export type SetCurrentProjectRequest = number | {
    projectId: number;
    /** Defaults to true so existing callers retain the current list-refresh behavior. */
    refreshProjects?: boolean;
};

export const setCurrentProject = createAsyncThunk("projects/setCurrentProject", async (request: SetCurrentProjectRequest, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.clientToken.token;
    const projectId = typeof request === "number" ? request : request.projectId;
    const refreshProjects = typeof request === "number" || request.refreshProjects !== false;

    if (!token) {
        return rejectWithValue("No token available");
    }

    try {
        const response = await setCurrentProjectApi(projectId, token);
        if (refreshProjects) {
            await dispatch(fetchProjects());
        }
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

// Async thunk to fetch a project by ID
export const fetchProjectById = createAsyncThunk("projects/fetchProjectById", async (projectId: string, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.clientToken.token;

    if (!token) {
        return rejectWithValue("No token available");
    }

    try {
        const response = await fetchProjectByIdAPI(projectId, token);
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

// Async thunk to fetch all projects
export const fetchProjects = createAsyncThunk<Projects[], void, { rejectValue: string }>(
    "projects/fetchProjects",
    async (_, { dispatch, getState, rejectWithValue }) => {
        const state = getState() as RootState;
        const token = state.clientToken.token;

        if (!token) {
            return rejectWithValue("No token available");
        }

        try {
            const response = await listUserProjectsApi(token);

            // Check if the response contains projects
            if (response.projects.length === 0) {
                const newProjectResponse = await dispatch(
                    createProject({
                        projectName: "My First Project",
                        projectDesc: "My First Project",
                        current: "true"
                    })
                ).unwrap();

                return [newProjectResponse.project];
            }

            // Return the list of projects
            return response.projects;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch projects");
        }
    }
);

// Async thunk to fetch project rebuild hierarchy
export const fetchProjectRebuildHierarchy = createAsyncThunk<ProjectRebuildHierarchy, number, { rejectValue: string }>(
    "projects/fetchRebuildHierarchy",
    async (projectId, { getState, rejectWithValue }) => {
        const state = getState() as RootState;
        const token = state.clientToken.token;

        if (!token) {
            return rejectWithValue("No token available");
        }

        try {
            const response = await getProjectRebuildHierarchyApi(projectId, token);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch project rebuild hierarchy");
        }
    }
);

const projectSlice = createSlice({
    name: "projects",
    initialState,
    reducers: {
        resetProjectState(state) {
            state.status = "idle";
            state.error = null;
        },
        setCurrentProjectId(state, action: { payload: number }) {
            state.currentProjectId = action.payload;
            // state.currentProjectId = action.payload;;
            console.log("state.currentProjectId: ", state.currentProjectId);
        },
        clearRebuildHierarchy(state) {
            state.rebuildHierarchy = null;
            state.rebuildHierarchyStatus = "idle";
            state.rebuildHierarchyError = null;
        }
    },
    extraReducers: builder => {
        builder
            // Handle createProject actions
            .addCase(createProject.pending, state => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(createProject.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.projects.push(action.payload.project);
                // If this is the first project or marked as current, set it as current
                // Note: Projects interface uses project_id (with underscore)
                if (state.projects.length === 1 || action.payload.project.current) {
                    state.currentProjectId = action.payload.project.project_id;
                }
            })
            .addCase(createProject.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
            })

            // Handle updateProject actions
            .addCase(updateProject.pending, state => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(updateProject.fulfilled, (state, action) => {
                state.status = "succeeded";
                const updatedProject = action.payload;
                const projectIndex = state.projects.findIndex(project => project.project_id === updatedProject.project_id);

                if (projectIndex !== -1) {
                    state.projects[projectIndex] = updatedProject;
                }

                if (state.currentProject?.project_id === updatedProject.project_id) {
                    state.currentProject = updatedProject;
                }
            })
            .addCase(updateProject.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
            })
            // Handle fetchProjects actions
            .addCase(fetchProjects.pending, state => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchProjects.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.projects = action.payload;
                // Set the first project or the current project as currentProjectId
                // Note: Projects interface uses project_id (with underscore)
                const currentProject = action.payload.find(p => p.current) || action.payload[0];
                if (currentProject && !state.currentProjectId) {
                    state.currentProjectId = currentProject.project_id;
                }
            })
            .addCase(fetchProjects.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message || "Failed to fetch projects";
            })
            // Handle fetchProjectById actions
            .addCase(fetchProjectById.pending, state => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchProjectById.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.currentProject = action.payload;
            })
            .addCase(fetchProjectById.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message || "Failed to fetch project details";
            })
            // Handle setCurrentProject actions
            .addCase(setCurrentProject.pending, state => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(setCurrentProject.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.currentProject = action.payload;
            })
            .addCase(setCurrentProject.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message || "Failed to set current project";
            })
            // Handle fetchProjectRebuildHierarchy actions
            .addCase(fetchProjectRebuildHierarchy.pending, state => {
                state.rebuildHierarchyStatus = "loading";
                state.rebuildHierarchyError = null;
            })
            .addCase(fetchProjectRebuildHierarchy.fulfilled, (state, action) => {
                state.rebuildHierarchyStatus = "succeeded";
                state.rebuildHierarchy = action.payload;
            })
            .addCase(fetchProjectRebuildHierarchy.rejected, (state, action) => {
                state.rebuildHierarchyStatus = "failed";
                state.rebuildHierarchyError = action.payload as string;
            });
    }
});

export const { resetProjectState, setCurrentProjectId, clearRebuildHierarchy } = projectSlice.actions;
export default projectSlice.reducer;

// ============================================
// Selectors for rebuild hierarchy
// ============================================

/**
 * Selector to check if an item needs rebuilding based on the rebuild hierarchy.
 *
 * Usage:
 * - needsRebuild(state) - returns product-level rebuild status
 * - needsRebuild(state, { featureId: 123 }) - returns feature-level rebuild status
 * - needsRebuild(state, { userStoryId: 456 }) - returns user story-level rebuild status
 * - needsRebuild(state, { featureId: 123, userStoryId: 456 }) - returns user story-level rebuild status
 *
 * @param state - The root Redux state
 * @param options - Optional object with featureId and/or userStoryId
 * @returns boolean indicating if the item needs rebuilding, or null if hierarchy not loaded
 */
export const selectNeedsRebuild = (
    state: RootState,
    options?: { featureId?: number; userStoryId?: number }
): boolean | null => {
    const hierarchy = state.projects.rebuildHierarchy;

    if (!hierarchy || !hierarchy.product) {
        return null; // Hierarchy not loaded yet
    }

    const { featureId, userStoryId } = options || {};

    // If userStoryId is provided, look for user story level
    if (userStoryId !== undefined) {
        for (const feature of hierarchy.product.features) {
            const userStory = feature.userstories.find(us => us.userstoryid === userStoryId);
            if (userStory) {
                return userStory.rebuild;
            }
        }
        return null; // User story not found
    }

    // If featureId is provided, look for feature level
    if (featureId !== undefined) {
        const feature = hierarchy.product.features.find(f => f.featureid === featureId);
        if (feature) {
            return feature.rebuild;
        }
        return null; // Feature not found
    }

    // Return product level rebuild status
    return hierarchy.product.rebuild;
};

/**
 * Selector to get the rebuild hierarchy from state
 */
export const selectRebuildHierarchy = (state: RootState) => state.projects.rebuildHierarchy;

/**
 * Selector to get the rebuild hierarchy loading status
 */
export const selectRebuildHierarchyStatus = (state: RootState) => state.projects.rebuildHierarchyStatus;
